import React, { useEffect, useRef } from "react";
import { Row, Col } from "react-bootstrap";
import StatsDashboard from "./components/StatsDashboard.js";
import InsurancePerformanceDashboard from "./components/InsurancePerformanceDashboard.js";
import ClaimsNumberDoughnutChart from "./components/ClaimsDoughnutChart.js";
import ClaimsGrossAmountDoughnutChart from "./components/ClaimsGrossAmountDoughnutChart.js";
import InsuranceMarketSharePieChart from "./components/InsuranceMarketSharePieChart.js";
import StatusBreakdownState from "./components/StatusBreakdownState";
import RejectionRateByInsurance from "./components/RejectionRateByInsurance";
import RejectionTrendChart from "./components/RejectionTrendChart";
import ClaimsProcessingFunnel from "./components/ClaimsProcessingFunnel";
import InsuranceMarketShareChart from "./components/InsuranceMarketShareChart";
import MonthlyClaimsVolumeTrend from "./components/MonthlyClaimsVolumeTrend.js";
import RejectionVsApprovedChart from "./components/RejectionVsApprovedChart.js";
import DepartmentPerformanceSummary from "./components/DepartmentPerformanceSummary.js";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { handleResetAll } from "features/preauthorization/config/preauthorizationSlice.js";
import {
  handlePreauthFormData,
  handleResetCreateClaim,
} from "features/cliams/config/claimSlice.js";
import { handleResetPatientSearch } from "features/eliglibility/eliglibility-check/config/eligibilityCheckSlice.js";
import { GetAllClaimDashboard } from "./config/claimDashboardApi.js";
import { handleClaimDashboardFilter } from "./config/claimDashboardSlice.js";
import ClaimDashboardFilter from "./components/ClaimDashboardFilter.js";
const ClaimDashboard = () => {
  const dispatch = useDispatch();
  const { claimDashboardFilters } = useSelector((state) => state.claimDashboard);
  const isInitialLoad = useRef(true);
  // Get current month in YYYY-MM format
  const getCurrentMonth = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}`;
  };

  const { watch, setValue, control, handleSubmit } = useForm({
    defaultValues: {
      branches: [],
      frm_mnth: getCurrentMonth(),
      to_mnth: getCurrentMonth(),
    },
  });

  const branchesValue = claimDashboardFilters.branches || [];
  const branch_id = branchesValue
    .map((item) => item?.branch_id)
    .filter(Boolean)
    .join(",") || "";

  const frm_mnth = claimDashboardFilters.frm_mnth || "";
  const to_mnth = claimDashboardFilters.to_mnth || "";
  const top_frm_mnth = claimDashboardFilters.top_frm_mnth || "";

  const onFilterSubmit = (formData) => {
    // Update Redux state with current form values
    dispatch(handleClaimDashboardFilter({ check: "branches", value: formData.branches || [] }));
    dispatch(handleClaimDashboardFilter({ check: "frm_mnth", value: formData.frm_mnth || "" }));
    dispatch(handleClaimDashboardFilter({ check: "to_mnth", value: formData.to_mnth || formData.frm_mnth || "" }));

    // Get current filter values for API call
    const branchesValue = formData.branches || [];
    const branch_id = branchesValue
      .map((item) => item?.branch_id)
      .filter(Boolean)
      .join(",") || "";

    const frm_mnth = formData.frm_mnth || "";
    const to_mnth = formData.to_mnth || formData.frm_mnth || "";
    const top_frm_mnth = claimDashboardFilters.top_frm_mnth || "";

    // Call API
    dispatch(
      GetAllClaimDashboard({
        branch_id,
        top_frm_mnth,
        frm_mnth,
        to_mnth,
      })
    );
  };



  useEffect(() => {
    dispatch(handleResetAll({ check: "reset" }));
    dispatch(handleResetCreateClaim());
    dispatch(handlePreauthFormData({ check: "reset" }));
    dispatch(handleResetPatientSearch());
  }, []);

  useEffect(() => {
    // Initialize branches with the login/default selected branch only once on mount
    try {
      const selectedBranch = JSON.parse(localStorage.getItem("selectedBranch"));
      const selectedBranchId = selectedBranch?.value || selectedBranch?.branch_id;
      const selectedBranchName = selectedBranch?.label || selectedBranch?.branch_name;
      const selectedBranchCchi =
        selectedBranch?.branch_cchi ||
        selectedBranch?.branch_cchino ||
        selectedBranch?.cchi;

      if (
        selectedBranchId &&
        (!claimDashboardFilters.branches || claimDashboardFilters.branches.length === 0)
      ) {
        const value = [
          {
            branch_id: selectedBranchId,
            branch_name: selectedBranchName,
            cchi: selectedBranchCchi,
          },
        ];
        dispatch(handleClaimDashboardFilter({ check: "branches", value }));
      }
    } catch (_) { }
  }, [dispatch]); // Only run once on mount, not when branches change

  // Watch for branch changes and handle initial load
  useEffect(() => {
    let lastBranchId = null;
    let isInitialLoad = true;

    const checkBranchChange = () => {
      try {
        const selectedBranch = JSON.parse(localStorage.getItem("selectedBranch"));
        const currentBranchId = selectedBranch?.value || selectedBranch?.branch_id;

        // If branch changed (or initial load) and we have a valid branch ID
        if (currentBranchId && (currentBranchId !== lastBranchId || isInitialLoad)) {
          lastBranchId = currentBranchId;
          isInitialLoad = false;

          // Update dashboard filters with the new branch
          const selectedBranchName = selectedBranch?.label || selectedBranch?.branch_name;
          const selectedBranchCchi = selectedBranch?.branch_cchi || selectedBranch?.branch_cchino || selectedBranch?.cchi;

          const value = [
            {
              branch_id: currentBranchId,
              branch_name: selectedBranchName,
              cchi: selectedBranchCchi,
            },
          ];
          dispatch(handleClaimDashboardFilter({ check: "branches", value }));
        }
      } catch (error) {
        console.error("Error checking branch change:", error);
      }
    };

    // Check immediately for initial load
    checkBranchChange();

    // Set up interval to check for changes
    const interval = setInterval(checkBranchChange, 1000);

    return () => {
      clearInterval(interval);
    };
  }, []);

  // Call API only on initial load when branch is initialized
  useEffect(() => {
    const branchesValue = claimDashboardFilters.branches || [];
    const currentBranchId = branchesValue
      .map((item) => item?.branch_id)
      .filter(Boolean)
      .join(",") || "";

    const currentFrmMnth = claimDashboardFilters.frm_mnth || getCurrentMonth();
    const currentToMnth = claimDashboardFilters.to_mnth || getCurrentMonth();
    const currentTopFrmMnth = claimDashboardFilters.top_frm_mnth || getCurrentMonth();

    // Only call API if we have a valid branch_id and this is the initial load
    if (currentBranchId && isInitialLoad.current) {
      isInitialLoad.current = false; // Mark that initial load is done
      dispatch(
        GetAllClaimDashboard({
          branch_id: currentBranchId,
          top_frm_mnth: currentTopFrmMnth,
          frm_mnth: currentFrmMnth,
          to_mnth: currentToMnth,
        })
      );
    }
  }, [claimDashboardFilters.branches]); // Only depend on branches, not on date changes

  return (
    <>
      <Row className="p-2">
        <Col xs={12}>
          <ClaimDashboardFilter
            watch={watch}
            control={control}
            setValue={setValue}
            onFilter={handleSubmit(onFilterSubmit)}
          />
        </Col>
      </Row>
      <Row className="g-3">
        <StatsDashboard />
        <InsurancePerformanceDashboard />
        {/* <ClaimsNumberDoughnutChart />
        <ClaimsGrossAmountDoughnutChart /> */}
        <StatusBreakdownState />
        <RejectionRateByInsurance />
        <RejectionTrendChart />
        {/* <ClaimsProcessingFunnel /> */}
        <InsuranceMarketSharePieChart />
        <InsuranceMarketShareChart />
        <MonthlyClaimsVolumeTrend />
        <RejectionVsApprovedChart />
        <DepartmentPerformanceSummary />
      </Row>
    </>
  );
};

export default ClaimDashboard;
